import { useLocation } from 'react-router-dom';
import { isPMRouteAuthorized, getPMRouteScope } from '@/config/pmRouteScopes';
import { usePMAuth } from '@/contexts/PMAuthContext';
import PMAccessDenied from '@/pages/pm/PMAccessDenied';

interface PMRouteGuardProps {
  children: React.ReactNode;
}

/**
 * Wraps every protected /pm/* page.
 * 1. If the route's status is 'access_denied' in PM_ROUTE_SCOPES → AccessDenied screen.
 * 2. If the route requires a permission the user doesn't have → AccessDenied screen.
 * 3. Otherwise renders the child page.
 *
 * This mirrors BaaSRouteGuard so PM users see a labeled access-denied UI consistently
 * instead of relying on each page to self-guard.
 */
export function PMRouteGuard({ children }: PMRouteGuardProps) {
  const { pathname } = useLocation();
  const { hasPermission } = usePMAuth();

  const normalizedPath = pathname.replace(/\/$/, '') || '/pm';
  const scope = getPMRouteScope(normalizedPath);

  if (!isPMRouteAuthorized(normalizedPath)) {
    return <PMAccessDenied requiredPermission={scope?.label} />;
  }

  if (scope?.permission && !hasPermission(scope.permission)) {
    return <PMAccessDenied requiredPermission={scope.permission} />;
  }

  return <>{children}</>;
}
